import React from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { Select, MenuItem, FormControl, Menu, ListItemIcon, IconButton, Dialog } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AppointmentDialog from './AppointmentDialog';
import { STATUS_COLORS, STATUS_TINTS, STATUS_LABEL_KEYS, SUMMARY_STATUSES } from './shared/appointmentStatus';

const SOURCE_ICONS = {
  web: 'zmdi-globe',
  android: 'zmdi-smartphone',
  ios: 'zmdi-apple',
  hermina_care: 'zmdi-mobile',
};

const INSURANCE_COLORS = { pending: '#B98900', approved: '#41924E', rejected: '#D14B48' };

export default class AppointmentTable extends React.Component {
  // Only the keys the table can actually act on. Anything else in the URL is ignored
  // rather than forwarded blindly to the API.
  static deepLinkParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      date: params.get('date') || '',
      hospital_id: params.get('hospital_id') || '',
      doctor_id: params.get('doctor_id') || '',
      q: params.get('q') || '',
    };
  }

  constructor(props) {
    super(props);
    // The calendar's day modal links here with the day and filters in the query
    // string. Without this the link lands on an unfiltered table and silently drops
    // everything the admin had narrowed to.
    const deepLink = AppointmentTable.deepLinkParams();
    this.deepLink = deepLink;
    this.state = {
      dialogOpen: false,
      selectedAppointment: null,
      dateField: 'date',
      dateFilter: deepLink.date ? 'on_date' : 'all_time',
      onDate: deepLink.date,
      statusFilter: '',
      statusCounts: null,
      menuAnchor: null,
      menuRow: null,
      editRow: null,
      editStatus: 0,
      editQueue: '',
      editSaving: false,
    };
    this.tableRef = React.createRef();
  }

  labels = () => this.props.labels || {};

  refreshTable = (resetPage = false) => {
    if (!this.tableRef.current) return;
    if (resetPage) this.tableRef.current.state.query.page = 0;
    this.tableRef.current.onQueryChange();
  };

  handleStatusChip = (status) => {
    const next = String(status) === String(this.state.statusFilter) ? '' : String(status);
    this.setState({ statusFilter: next }, () => this.refreshTable(true));
  };

  handleRowClick = (_event, rowData) => {
    this.setState({ dialogOpen: true, selectedAppointment: rowData });
  };

  handleCloseDialog = () => {
    this.setState({ dialogOpen: false, selectedAppointment: null });
  };

  openMenu = (event, rowData) => {
    event.stopPropagation();
    this.setState({ menuAnchor: event.currentTarget, menuRow: rowData });
  };

  closeMenu = () => this.setState({ menuAnchor: null, menuRow: null });

  // Rails-UJS style form post so reject/delete keep CSRF protection and the
  // server's redirect + flash behaviour.
  postForm = (action, method) => {
    const tokenTag = document.querySelector('meta[name="csrf-token"]');
    const form = document.createElement('form');
    form.method = 'post';
    form.action = action;
    const add = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    if (method !== 'post') add('_method', method);
    if (tokenTag) add('authenticity_token', tokenTag.content);
    document.body.appendChild(form);
    form.submit();
  };

  menuAction = (action) => {
    const row = this.state.menuRow;
    if (!row) return;
    const labels = this.labels();
    const url = row.attributes.url;
    this.closeMenu();

    switch (action) {
      case 'preview':
        this.setState({ dialogOpen: true, selectedAppointment: row });
        break;
      case 'open':
        Turbolinks.visit(url);
        break;
      case 'edit':
        // Inline status edit, same contract the old material-table row editor
        // used: PUT /api/appointments/:slug with attributes.status/queue_number.
        this.setState({
          editRow: row,
          editStatus: row.attributes.status,
          editQueue: row.attributes.queue_number || '',
        });
        break;
      case 'reject':
        // Rejection needs a reason, so hand over to the detail page's
        // guarantor form, which auto-opens preset to rejected.
        window.location.assign(`${url}?reject=1`);
        break;
      case 'delete':
        if (window.confirm(labels.delete_confirm)) this.postForm(url, 'delete');
        break;
      default:
        break;
    }
  };

  closeEdit = () => this.setState({ editRow: null, editSaving: false });

  saveEdit = () => {
    const row = this.state.editRow;
    if (!row) return;
    this.setState({ editSaving: true });
    fetch(`/api/appointments/${row.attributes.slug}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { status: this.state.editStatus, queue_number: this.state.editQueue },
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.closeEdit();
        this.refreshTable();
      })
      .catch(() => this.setState({ editSaving: false }));
  };

  statusBadge = (status) => {
    const labels = this.labels();
    return (
      <span
        className="appt-table__badge"
        style={{ backgroundColor: STATUS_TINTS[status] || STATUS_TINTS.default, color: STATUS_COLORS[status] || STATUS_COLORS.default }}
      >
        <i className="appt-table__badge-dot" style={{ backgroundColor: STATUS_COLORS[status] || STATUS_COLORS.default }} />
        {labels[STATUS_LABEL_KEYS[status]] || 'Unknown'}
      </span>
    );
  };

  columns = () => {
    const labels = this.labels();
    const lockedToHospital = !!this.props.hospital_id;

    return [
      {
        title: labels.col_patient,
        field: 'attributes.name',
        editable: 'never',
        render: (row) => (
          <div className="appt-table__patient">
            <span className="appt-table__avatar">
              <i className={`zmdi ${SOURCE_ICONS[row.attributes.source] || 'zmdi-globe'}`} />
            </span>
            <span className="appt-table__patient-text">
              <a
                href={row.attributes.url}
                className="appt-table__patient-name"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  Turbolinks.visit(row.attributes.url);
                }}
              >
                {row.attributes.name}
              </a>
              <small>
                {row.attributes.medical_record_number
                  ? `${labels.mrn} ${row.attributes.medical_record_number}`
                  : labels.no_mrn}
              </small>
            </span>
          </div>
        ),
      },
      {
        title: labels.col_status,
        field: 'attributes.status',
        editable: 'never',
        lookup: SUMMARY_STATUSES.reduce((acc, s) => ({ ...acc, [s]: labels[STATUS_LABEL_KEYS[s]] }), {}),
        render: (row) => this.statusBadge(row.attributes.status),
      },
      {
        title: labels.col_schedule,
        field: 'attributes.date',
        editable: 'never',
        render: (row) => (
          <div className="appt-table__stack">
            <span>{row.attributes.date}</span>
            <small>{row.attributes.time_label || '-'}</small>
          </div>
        ),
      },
      { title: labels.col_dob, field: 'attributes.dob', editable: 'never' },
      {
        title: labels.col_doctor,
        field: 'attributes.doctor_name',
        editable: 'never',
        render: (row) => (
          <div className="appt-table__stack">
            <span>{row.attributes.doctor_name}</span>
            <small>{row.attributes.doctor_speciality_name || '-'}</small>
          </div>
        ),
      },
      {
        // A branch admin's rows are all one hospital, so the column starts in the
        // column picker instead of doubling DOB up (PE-436 defect 1).
        title: labels.col_hospital,
        field: 'attributes.hospital_name',
        editable: 'never',
        hidden: lockedToHospital,
      },
      {
        title: labels.col_payment,
        field: 'attributes.payment_method',
        editable: 'never',
        render: (row) => (
          <div className="appt-table__stack">
            <span>{row.attributes.payment_method}</span>
            {row.attributes.insurance_status ? (
              <small style={{ color: INSURANCE_COLORS[row.attributes.insurance_status] || undefined, fontWeight: 600 }}>
                {labels[`status_${row.attributes.insurance_status}`] || row.attributes.insurance_status}
              </small>
            ) : (
              <small>{labels.no_insurance}</small>
            )}
          </div>
        ),
      },
      {
        title: labels.col_created,
        field: 'attributes.created_at',
        editable: 'never',
        render: (row) => {
          const parts = String(row.attributes.created_at).split(' - ');
          return (
            <div className="appt-table__stack">
              <span>{parts[0]}</span>
              <small>{parts[1] || ''}</small>
            </div>
          );
        },
      },
      { title: labels.col_phone, field: 'attributes.mobile_phone', editable: 'never', hidden: true },
      {
        title: '',
        field: 'actions',
        sorting: false,
        export: false,
        width: 48,
        render: (row) => (
          <IconButton size="small" className="appt-table__menu-btn" onClick={(e) => this.openMenu(e, row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
    ];
  };

  fetchData = (query) =>
    new Promise((resolve, reject) => {
      const hospitalId = this.props.hospital_id || this.deepLink.hospital_id;
      const doctorId = this.props.doctor_id || this.deepLink.doctor_id;
      let url = `/api/appointments?per_page=${query.pageSize}`;
      url += `&q=${encodeURIComponent(query.search || '')}`;
      if (hospitalId) url += `&hospital_id=${hospitalId}`;
      if (doctorId) url += `&doctor_id=${doctorId}`;
      url += `&page=${query.page + 1}`;
      url += `&date_field=${this.state.dateField}`;
      url += `&date_filter=${this.state.dateFilter}`;
      if (this.state.dateFilter === 'on_date' && this.state.onDate) url += `&date=${this.state.onDate}`;
      if (this.state.statusFilter !== '') url += `&status=${this.state.statusFilter}`;

      fetch(url)
        .then((response) => response.json())
        .then((result) => {
          this.setState({ statusCounts: result.status_counts || {} });
          resolve({
            data: result.accounts.data,
            page: result.pagination.page - 1,
            totalCount: result.pagination.count,
          });
        })
        .catch((error) => reject(error));
    });

  renderChips = () => {
    const labels = this.labels();
    const counts = this.state.statusCounts || {};
    const keyFor = { 0: 'unconfirmed', 1: 'confirmed', 2: 'canceled', 3: 'checked_in', 4: 'completed', 5: 'upcoming' };
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    const active = this.state.statusFilter;

    return (
      <div className="appt-chips">
        <button
          type="button"
          className={`appt-chips__chip ${active === '' ? 'is-active' : ''}`}
          onClick={() => this.handleStatusChip('')}
        >
          {labels.chip_all}
          {this.state.statusCounts && <span className="appt-chips__count">{total.toLocaleString()}</span>}
        </button>
        {SUMMARY_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            className={`appt-chips__chip ${active === String(status) ? 'is-active' : ''}`}
            onClick={() => this.handleStatusChip(status)}
          >
            <i className="appt-chips__dot" style={{ backgroundColor: STATUS_COLORS[status] }} />
            {labels[STATUS_LABEL_KEYS[status]]}
            {this.state.statusCounts && (
              <span className="appt-chips__count">{(counts[keyFor[status]] || 0).toLocaleString()}</span>
            )}
          </button>
        ))}
      </div>
    );
  };

  renderToolbar = (props) => (
    <div className="appt-toolbar">
      {this.renderChips()}
      <div className="appt-toolbar__filters">
        <FormControl variant="outlined" size="small" className="appt-toolbar__select">
          <Select value={this.state.dateField} onChange={(e) => this.setState({ dateField: e.target.value }, () => this.refreshTable(true))}>
            {(this.props.date_field_options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className="appt-toolbar__select">
          <Select value={this.state.dateFilter} onChange={(e) => this.setState({ dateFilter: e.target.value }, () => this.refreshTable(true))}>
            {this.state.onDate && <MenuItem value="on_date">{this.state.onDate}</MenuItem>}
            {(this.props.date_filter_options || []).map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <div className="appt-toolbar__search">
          <MTableToolbar
            {...props}
            showTitle={false}
            searchFieldAlignment="left"
            toolbarButtonAlignment="right"
            classes={{
              root: 'appt-toolbar__mt-root',
              actions: 'appt-toolbar__mt-actions',
              spacer: 'appt-toolbar__mt-spacer',
              searchField: 'appt-toolbar__mt-search',
            }}
          />
        </div>
      </div>
    </div>
  );

  getTheme = () => {
    const isDark = document.documentElement.getAttribute('data-mode-theme') === 'dark';
    return createTheme({
      palette: {
        type: isDark ? 'dark' : 'light',
        primary: { main: '#41924E' },
        background: { paper: isDark ? '#212830' : '#ffffff', default: isDark ? '#151C23' : '#F5F6FA' },
      },
      typography: { fontFamily: 'inherit' },
    });
  };

  render() {
    const labels = this.labels();
    const menuRow = this.state.menuRow;
    const showingOf = (labels.showing_of || '{from}-{to} / {count}')
      .replace('%{shown}', '{from}-{to}')
      .replace('%{total}', '{count}');

    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <div className="appt-table">
          <MaterialTable
            tableRef={this.tableRef}
            title=""
            columns={this.columns()}
            data={this.fetchData}
            onRowClick={this.handleRowClick}
            components={{ Toolbar: this.renderToolbar }}
            actions={[
              {
                icon: 'refresh',
                tooltip: labels.refresh,
                isFreeAction: true,
                onClick: () => this.refreshTable(),
              },
            ]}
            localization={{
              toolbar: { searchPlaceholder: labels.search_placeholder, searchTooltip: labels.search_placeholder },
              pagination: { labelDisplayedRows: showingOf },
              header: { actions: '' },
            }}
            options={{
              pageSize: 10,
              pageSizeOptions: [10, 25, 50, 100, 500],
              exportButton: true,
              columnsButton: true,
              sorting: true,
              search: true,
              searchFieldStyle: { width: '100%' },
              grouping: false,
              tableLayout: 'auto',
              actionsColumnIndex: -1,
              headerStyle: { whiteSpace: 'nowrap' },
            }}
          />

          <Menu
            anchorEl={this.state.menuAnchor}
            open={Boolean(this.state.menuAnchor)}
            onClose={this.closeMenu}
            className="appt-row-menu"
          >
            <MenuItem onClick={() => this.menuAction('preview')}>
              <ListItemIcon><VisibilityOutlinedIcon fontSize="small" /></ListItemIcon>
              {labels.action_preview}
            </MenuItem>
            <MenuItem onClick={() => this.menuAction('open')}>
              <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
              {labels.action_open}
            </MenuItem>
            <MenuItem onClick={() => this.menuAction('edit')}>
              <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
              {labels.action_edit}
            </MenuItem>
            {menuRow && menuRow.attributes.profile_insurance_id && (
              <MenuItem onClick={() => this.menuAction('reject')}>
                <ListItemIcon><CancelOutlinedIcon fontSize="small" /></ListItemIcon>
                {labels.action_reject}
              </MenuItem>
            )}
            {menuRow && menuRow.attributes.status === 5 && (
              <MenuItem onClick={() => this.menuAction('delete')} className="appt-row-menu__danger">
                <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
                {labels.action_delete}
              </MenuItem>
            )}
          </Menu>

          <AppointmentDialog
            open={this.state.dialogOpen}
            onClose={this.handleCloseDialog}
            appointment={this.state.selectedAppointment}
            labels={labels}
          />

          <Dialog open={Boolean(this.state.editRow)} onClose={this.closeEdit} maxWidth="xs" fullWidth classes={{ paper: 'appt-edit' }}>
            {this.state.editRow && (
              <div className="appt-edit__body">
                <div className="appt-edit__title">{labels.action_edit}</div>
                <div className="appt-edit__name">{this.state.editRow.attributes.name}</div>
                <div className="form-group">
                  <label className="appt-edit__label">{labels.col_status}</label>
                  <select
                    className="form-control"
                    value={this.state.editStatus}
                    onChange={(e) => this.setState({ editStatus: Number(e.target.value) })}
                  >
                    {SUMMARY_STATUSES.map((status) => (
                      <option key={status} value={status}>{labels[STATUS_LABEL_KEYS[status]]}</option>
                    ))}
                  </select>
                  <i className="form-group__bar" />
                </div>
                <div className="form-group">
                  <label className="appt-edit__label">{labels.edit_queue}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.editQueue}
                    onChange={(e) => this.setState({ editQueue: e.target.value })}
                  />
                  <i className="form-group__bar" />
                </div>
                <div className="appt-edit__footer">
                  <button type="button" className="appt-qv__quiet-btn" onClick={this.closeEdit}>{labels.qv_close}</button>
                  <button type="button" className="btn btn-primary" disabled={this.state.editSaving} onClick={this.saveEdit}>
                    {labels.save}
                  </button>
                </div>
              </div>
            )}
          </Dialog>
        </div>
      </MuiThemeProvider>
    );
  }
}
